/** Index book lookup methods, following the original ImageSync (ImageFlowUtilities and its database procedures).
 *  Steps use `backticks` for values.
 *
 *  The original kept a lookup table per index book (tblIndexBookList names it) and read it one of two ways:
 *
 *  Keyed methods (LMNRT, SimpleLMNRT, SimpleAlpha, AlphaFirstInitial): the surname becomes a key, the key picks a row
 *  of the table, and the given-name initial picks the column (the last column is for corporations). The cell is the
 *  section number. Only the key rule differs:
 *    russell (LMNRT)                   drop the first letter, keep L M N R T, collapse repeats, keep the first two
 *    simple-lmnrt (SimpleLMNRT)        the first L, M, N, R or T after the first letter
 *    alpha (SimpleAlpha)               the first letter of the surname
 *    alpha-initial (AlphaFirstInitial) the first letter of the surname
 *  (the original ran the two alphabetical methods through the same code; a book's table either has a column per
 *  given-name range or a single section column, and the lookup follows the table.)
 *
 *  Beginnings (Cott, Graves): each row is a beginning of the surname with its section. Cott takes the longest beginning
 *  the name starts with (`'<name>' LIKE LastNameKey + '%' … ORDER BY LEN(LastNameKey) DESC`); a row marked ExactNameOnly
 *  applies to that name only. Graves (spGetGravesIndexSection) looks for the full name first, then the first two
 *  letters with the third among the letters listed for them (or none listed), then the first letter alone, then a
 *  catch-all row. Both come down to "the most specific listed beginning the name fits".
 *
 *  Here a book's table is its list of keys (`IndexKey`): section, key, column (or the letters that may follow a Graves
 *  beginning) and the exact flag. A book with no list falls back to the standard grid for the keyed methods. */
/** The original's key grid for the County Clerk's Russell books: the key rows and the given-name columns. */
export const RUSSELL_KEYS = ['—', 'L', 'M', 'N', 'R', 'T', 'LM', 'LN', 'LR', 'LT', 'ML', 'MN', 'MR', 'MT', 'NL', 'NM', 'NR', 'NT', 'RL', 'RM', 'RN', 'RT', 'TL', 'TM', 'TN', 'TR'];
export const RUSSELL_COLS = ['A–E', 'F–J', 'K–O', 'P–T', 'U–Z', 'CORP.'];
export const RUSSELL_COL_RANGES = [['A', 'E'], ['F', 'J'], ['K', 'O'], ['P', 'T'], ['U', 'Z']];
export const SIMPLE_LMNRT_KEYS = ['—', 'L', 'M', 'N', 'R', 'T'];
export const ALPHA_KEYS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
export const NOKEY = '—';
export const INDEX_METHODS = [
    { id: 'russell', label: 'Russell (L-M-N-R-T key)', short: 'Russell', legacy: 'LMNRT', aliases: ['russel', 'lmnrt'], keyed: true, usesInitial: true, needsInitial: true, usesCorporation: true, lastNameOnly: false, defaultCols: RUSSELL_COLS,
        summary: 'names are keyed by the letters L, M, N, R and T after the first letter, then filed by the given-name initial.' },
    { id: 'simple-lmnrt', label: 'Simple L-M-N-R-T key', short: 'Simple LMNRT', legacy: 'SimpleLMNRT', aliases: [], keyed: true, usesInitial: true, needsInitial: true, usesCorporation: true, lastNameOnly: false, defaultCols: RUSSELL_COLS,
        summary: 'names are keyed by the first L, M, N, R or T after the first letter, then filed by the given-name initial.' },
    { id: 'alpha', label: 'Alphabetical', short: 'Alphabetical', legacy: 'SimpleAlpha', aliases: ['alphabetical'], keyed: true, usesInitial: true, needsInitial: false, usesCorporation: true, lastNameOnly: false, defaultCols: [],
        summary: 'names are filed by the first letter of the surname, and by the given-name initial where the book divides a letter that way.' },
    { id: 'alpha-initial', label: 'Alphabetical with given-name initial', short: 'Alpha + initial', legacy: 'AlphaFirstInitial', aliases: ['alphafirstinital'], keyed: true, usesInitial: true, needsInitial: true, usesCorporation: true, lastNameOnly: false, defaultCols: RUSSELL_COLS,
        summary: 'names are filed by the first letter of the surname, then by the given-name initial.' },
    { id: 'cott', label: 'Cott (surname beginnings)', short: 'Cott', legacy: 'Cott', aliases: ['cotts'], keyed: false, usesInitial: false, needsInitial: false, usesCorporation: false, lastNameOnly: true, defaultCols: [],
        summary: 'names are filed by the letters the surname begins with.' },
    { id: 'graves', label: 'Graves', short: 'Graves', legacy: 'Graves', aliases: [], keyed: false, usesInitial: false, needsInitial: false, usesCorporation: false, lastNameOnly: true, defaultCols: [],
        summary: 'names are filed by their first letters: the first two, and the third among those the book lists for them; a few common surnames have a section of their own.' },
];
const BY_ID = new Map(INDEX_METHODS.map(m => [m.id, m]));
export const methodInfo = (id) => BY_ID.get(id) ?? INDEX_METHODS[0];
export const isIndexMethod = (id) => BY_ID.has(id);
/** The method by its id or the original's name (LMNRT, SimpleLMNRT, SimpleAlpha, AlphaFirstInitial, Cott, Graves),
 *  spelling and case aside (the original's data has `AlphaFirstInital`). */
export function parseMethod(name) {
    const n = name.trim().toLowerCase().replace(/[\s_-]/g, '');
    for (const m of INDEX_METHODS)
        if (m.id.replace(/-/g, '') === n || m.legacy.toLowerCase() === n || m.aliases.includes(n))
            return m.id;
    return null;
}
/* ---------------------------------------------------------------- keys */
const letters = (s) => s.toUpperCase().replace(/[^A-Z]/g, '');
const firstWord = (s, corp) => (corp ? s.trim().split(/\s+/)[0] ?? '' : s);
export function russellKey(word) {
    const up = letters(word);
    const rest = up.slice(1);
    const kept = rest.replace(/[^LMNRT]/g, '');
    let dedup = '';
    for (const c of kept)
        if (!dedup || dedup[dedup.length - 1] !== c)
            dedup += c;
    return { up, rest, kept, dedup, key: dedup.length < 3 ? dedup : dedup.slice(0, 2) };
}
export function simpleLmnrtKey(word) {
    const up = letters(word);
    const rest = up.slice(1), kept = rest.replace(/[^LMNRT]/g, '');
    return { up, rest, kept, key: kept.slice(0, 1) };
}
export const alphaKey = (word) => { const up = letters(word); return { up, key: up.slice(0, 1) }; };
/** Read a column label: 'A–E', 'A_E', 'A-E', 'AtoE' and 'AE' all cover A to E; 'A' covers A; 'HI' covers H to I;
 *  'CORP.' or 'Corporation' is corporations and covers no initial. */
export function parseColumn(label) {
    const s = label.trim();
    if (/corp|compan|firm/i.test(s))
        return { label: s, corporation: true };
    const ls = letters(s.replace(/\bto\b/gi, ' '));
    if (!ls)
        return { label: s };
    return { label: s, from: ls[0], to: ls[ls.length - 1] < ls[0] ? ls[0] : ls[ls.length - 1] };
}
export function parseColumns(cfg = {}, method = 'russell') {
    const info = methodInfo(method);
    const labels = cfg.cols ?? info.defaultCols;
    const cols = labels.map(parseColumn);
    if (cfg.colRanges)
        cfg.colRanges.forEach(([a, b], i) => { if (cols[i] && !cols[i].corporation) {
            cols[i].from = a.toUpperCase();
            cols[i].to = b.toUpperCase();
        } });
    // the original takes the last column for corporations whatever it's called (it keeps its initials too)
    if (info.usesCorporation && cols.length && !cols.some(c => c.corporation))
        cols[cols.length - 1].corporation = true;
    return cols;
}
export function columnFor(cols, initial, corporation) {
    if (corporation)
        return cols.findIndex(c => c.corporation);
    const ch = initial.toUpperCase().slice(0, 1);
    if (!ch)
        return -1;
    return cols.findIndex(c => c.from !== undefined && ch >= c.from && ch <= c.to);
}
/** The original compared letter ranges loosely; 'A–E', 'A_E' and 'AE' are the same column. */
export const sameColumn = (a, b) => !!a && !!b && letters(a) === letters(b);
export const sameKey = (a, b) => (letters(a ?? '') || NOKEY) === (letters(b ?? '') || NOKEY);
const spaced = (k) => (!k || k === NOKEY ? '(none)' : k.split('').join(' '));
const defaultKeys = (method) => (method === 'russell' ? RUSSELL_KEYS : method === 'simple-lmnrt' ? SIMPLE_LMNRT_KEYS : ALPHA_KEYS);
/** The columns a book's key list uses, when the configuration doesn't name them: as the county's table had them. */
export function columnsFromLabels(keys) {
    const seen = new Map();
    for (const k of [...keys].sort((a, b) => a.section - b.section))
        if (k.col && !seen.has(letters(k.col)))
            seen.set(letters(k.col), k.col);
    return [...seen.values()];
}
/** The key and column for a name, by the method; the section comes from `sectionFor`. */
export function keyFor(method, surname, opts, cfg = {}) {
    const info = methodInfo(method);
    const corp = info.usesCorporation && !!opts.corporation;
    const word = firstWord(surname, corp);
    const cols = parseColumns(cfg, method);
    const steps = [];
    let key, up;
    if (method === 'russell') {
        const t = russellKey(word);
        up = t.up;
        key = t.key || NOKEY;
        steps.push('Drop the first letter: `' + t.up + '` → `' + (t.rest || NOKEY) + '`', 'Keep only L, M, N, R and T' + (t.kept !== t.dedup ? ', counting side-by-side repeats once' : '') + ': `' + (t.dedup || 'none') + '`', t.key ? 'The first two form the key: `' + spaced(t.key) + '`' : 'No key letters, so the name goes on the first line of the guide.');
    }
    else if (method === 'simple-lmnrt') {
        const t = simpleLmnrtKey(word);
        up = t.up;
        key = t.key || NOKEY;
        steps.push('Drop the first letter: `' + t.up + '` → `' + (t.rest || NOKEY) + '`', t.key ? 'The first L, M, N, R or T is the key: `' + t.key + '`' : 'No L, M, N, R or T, so the name goes on the first line of the guide.');
    }
    else {
        const t = alphaKey(word);
        up = t.up;
        key = t.key || NOKEY;
        steps.push('The first letter of the surname `' + t.up + '` is the key: `' + key + '`');
    }
    let columnIndex = -1;
    if (info.usesInitial && cols.length && (corp || opts.initial)) {
        columnIndex = columnFor(cols, opts.initial ?? '', corp);
        steps.push(corp ? 'Corporation: use the `' + (cols[columnIndex]?.label ?? 'CORP.') + '` column.'
            : 'Given-name initial `' + (opts.initial ?? '').toUpperCase() + '` falls in column `' + (cols[columnIndex]?.label ?? '?') + '`.');
    }
    return { book: up.slice(0, 1), key, columnIndex, ...(columnIndex >= 0 ? { column: cols[columnIndex].label } : {}), steps };
}
/** The section for a key: from the book's key list when it has one (the county's lookup table), by the column the
 *  initial picked where the table has columns; otherwise from the grid, as the original numbers it
 *  (row × columns + column + 1). */
export function sectionFor(method, k, keys, cfg = {}, opts = {}) {
    const listed = keys.filter(l => sameKey(l.key, k.key));
    // why no column was picked, where the book divides a key by column
    const noColumn = (labels) => opts.corporation ? 'The book has no corporation column.'
        : opts.initial ? 'None of the book’s columns (`' + labels.join('` `') + '`) covers the initial `' + opts.initial.toUpperCase() + '`.'
            : 'Book `' + k.book + '` files that key by the given-name initial too: enter the initial.';
    if (listed.length) {
        const withCols = listed.filter(l => l.col);
        if (!withCols.length)
            return { section: listed[0].section, found: 'list', step: 'The book’s list files it in section `' + pad4(listed[0].section) + '`.' };
        if (!k.column)
            return { section: null, found: 'none', step: noColumn(columnsFromLabels(keys)) };
        const hit = withCols.find(l => sameColumn(l.col, k.column));
        if (hit)
            return { section: hit.section, found: 'list', step: 'The book’s list files it in section `' + pad4(hit.section) + '`.' };
        return { section: null, found: 'none', step: 'The book’s list has that key, but no `' + k.column + '` column.' };
    }
    if (cfg.listed)
        return { section: null, found: 'none', step: 'The book’s list has no `' + (letters(k.key) || NOKEY) + '` key, so the name isn’t filed in it.' };
    const rows = (cfg.keys ?? defaultKeys(method)).map(x => letters(x) || NOKEY);
    const cols = parseColumns(cfg, method);
    const row = rows.indexOf(letters(k.key) || NOKEY);
    const width = cols.length || 1;
    const col = cols.length ? k.columnIndex : 0;
    if (row < 0)
        return { section: null, found: 'none', step: 'No row of the guide has the key `' + (letters(k.key) || NOKEY) + '`.' };
    if (col < 0)
        return { section: null, found: 'none', step: noColumn(cols.map(c => c.label)) };
    const section = row * width + col + 1;
    return { section, found: 'grid', step: 'Row `' + (row + 1) + '` of the guide' + (cols.length ? ', column `' + (col + 1) + '`' : '') + ': section `' + pad4(section) + '`.' };
}
/** A keyed lookup from end to end: the key, the column, and the section from the book's key list or its grid. */
export function keyedLookup(method, surname, opts, cfg = {}, keys = []) {
    if (!cfg.cols) {
        const cols = columnsFromLabels(keys);
        if (cols.length)
            cfg = { ...cfg, cols };
    }
    const k = keyFor(method, surname, opts, cfg);
    const s = sectionFor(method, k, keys, cfg, opts);
    return { book: k.book, section: s.section, steps: [...k.steps, s.step], key: k.key, ...(k.column ? { column: k.column } : {}), found: s.found };
}
/** The Russell lookup as before, for callers that still use it. */
export const russellLookup = (surname, opts, cfg = {}, keys = []) => keyedLookup('russell', surname, opts, cfg, keys);
/* ---------------------------------------------------------------- beginnings: Cott and Graves */
/** A listed beginning that the surname fits: the name itself when the row is exact; otherwise the name starts with the
 *  beginning and, for a Graves row that lists the letters that may follow, its next letter is one of them (a name
 *  that ends at the beginning fits, as it did in the original's procedure). */
export function fitsBeginning(s, k) {
    if (k.exact)
        return s === letters(k.key);
    const b = letters(k.key);
    if (!s.startsWith(b))
        return false;
    const next = s[b.length];
    return !k.col || next === undefined || letters(k.col).includes(next);
}
/** The most specific beginning: an exact name first, then the longest beginning, a listed set of next letters
 *  beating none (the Graves procedure's order of tries). */
const specificity = (k) => (k.exact ? 1000 : letters(k.key).length * 2 + (k.col ? 1 : 0));
/** Cott lookup: the longest listed beginning the surname starts with; beginnings marked exact apply to that name only. */
export function cottLookup(surname, keys) {
    const s = letters(surname);
    const agree = keys.filter(k => fitsBeginning(s, k));
    const best = agree.reduce((a, c) => (!a || specificity(c) > specificity(a) ? c : a), null);
    const skipped = keys.filter(k => k.exact && s !== letters(k.key) && s.startsWith(letters(k.key)));
    const steps = ['Surname: `' + s + '`', 'Beginnings in the book’s list that it starts with: ' + (agree.length ? agree.map(c => '`' + letters(c.key) + '`').join(' ') : 'none')];
    if (best)
        steps.push(best.exact ? '`' + letters(best.key) + '` is filed on its own, in section `' + pad4(best.section) + '`.' : 'The longest is `' + letters(best.key) + '`, so the name is in section `' + pad4(best.section) + '`.');
    if (skipped.length)
        steps.push(skipped.map(c => '`' + letters(c.key) + '`').join(' ') + ' is listed for that exact name only, so it doesn’t apply.');
    return { book: s.slice(0, 1), section: best ? best.section : null, steps, found: best ? 'list' : 'none' };
}
/** Graves lookup, as spGetGravesIndexSection did it: the full name if it is listed on its own; else the row for the
 *  first two letters whose third letters include the name's (or that lists none); else the first letter alone; else
 *  the book's catch-all row. */
export function gravesLookup(surname, keys) {
    const s = letters(surname);
    const [a, b, c] = [s[0] ?? '', s[1] ?? '', s[2] ?? ''];
    const agree = keys.filter(k => fitsBeginning(s, k));
    const best = agree.reduce((x, k) => (!x || specificity(k) > specificity(x) ? k : x), null);
    const steps = ['Surname: `' + s + '`, first letters `' + a + '` `' + b + '`' + (c ? ' then `' + c + '`' : '')];
    if (!best)
        steps.push('The book’s list has no beginning the name fits' + (keys.length ? '.' : ', and no list has been loaded for it.'));
    else if (best.exact)
        steps.push('`' + s + '` is listed on its own, in section `' + pad4(best.section) + '`.');
    else if (!letters(best.key))
        steps.push('No beginning is listed for it: the book’s catch-all row, section `' + pad4(best.section) + '`.');
    else
        steps.push('Beginning `' + letters(best.key) + '`' + (best.col ? ' with the third letter among `' + letters(best.col) + '`' : letters(best.key).length > 1 ? ', whatever follows' : ' alone') + ': section `' + pad4(best.section) + '`.');
    return { book: a, section: best ? best.section : null, steps, found: best ? 'list' : 'none' };
}
/** Any method's lookup against a book's key list (and grid configuration, for the keyed methods). */
export function lookupSection(method, surname, opts, cfg = {}, keys = []) {
    if (method === 'cott')
        return cottLookup(surname, keys);
    if (method === 'graves')
        return gravesLookup(surname, keys);
    return keyedLookup(method, surname, opts, cfg, keys);
}
/** Soundex exactly as PostgreSQL's fuzzystrmatch computes it (the estate search runs it in the database; this copy is
 *  for tests and tools). Each letter is compared with the character just before it, so a vowel, H, W or Y between two
 *  letters with the same code separates them (ASHCRAFT is A226), unlike the census rule. */
export function soundex(input) {
    const CODES = '01230120022455012623010202'; // A..Z
    const code = (ch) => (ch >= 'A' && ch <= 'Z' ? CODES[ch.charCodeAt(0) - 65] : ch);
    const s = input.toUpperCase();
    let i = 0;
    while (i < s.length && !(s[i] >= 'A' && s[i] <= 'Z'))
        i++;
    if (i >= s.length)
        return '';
    let out = s[i];
    for (i++; i < s.length && out.length < 4; i++) {
        const ch = s[i];
        if (ch >= 'A' && ch <= 'Z' && code(ch) !== code(s[i - 1])) {
            const c = code(ch);
            if (c !== '0')
                out += c;
        }
    }
    return out.padEnd(4, '0');
}
export const pad4 = (n) => String(n).padStart(4, '0');
/* ---------------------------------------------------------------- the letters a book covers */
/** A set of first letters as the original kept it (tblIndexBookList.Letter: 'A', 'IJ', 'XYZ'), from however it is
 *  written: 'A', 'A-C', 'A–C', 'A to C', 'IJ', 'A-C, X'. In capitals, sorted, each letter once; null when it says
 *  nothing usable. */
export function letterSet(spec) {
    const out = new Set();
    for (const part of spec.toUpperCase().replace(/\s+TO\s+/g, '-').split(/[,;\s]+/).filter(Boolean)) {
        const m = /^([A-Z])(?:-|–|TO)([A-Z])$/.exec(part);
        if (m) {
            const [a, b] = m[1] <= m[2] ? [m[1], m[2]] : [m[2], m[1]];
            for (let c = a.charCodeAt(0); c <= b.charCodeAt(0); c++)
                out.add(String.fromCharCode(c));
            continue;
        }
        if (!/^[A-Z]+$/.test(part))
            return null;
        for (const ch of part)
            out.add(ch);
    }
    return out.size ? [...out].sort().join('') : null;
}
/** The letters as people write them: 'ABC' → 'A–C', 'IJY' → 'I–J, Y', 'ABCDEFGHIJKLMNOPQRSTUVWXYZ' → 'A–Z'. */
export function letterRanges(set) {
    const ls = [...new Set(set.toUpperCase().replace(/[^A-Z]/g, ''))].sort();
    const parts = [];
    for (let i = 0; i < ls.length;) {
        let j = i;
        while (j + 1 < ls.length && ls[j + 1].charCodeAt(0) === ls[j].charCodeAt(0) + 1)
            j++;
        parts.push(j > i ? ls[i] + '–' + ls[j] : ls[i]);
        i = j + 1;
    }
    return parts.join(', ');
}
/** The surnames a book covers, from its label when nothing says otherwise: Book N covers N, Book A–C covers A to C,
 *  Book "R A-M" covers R (the office file or the import gives the initials), a book with no letter in its label covers
 *  everything. */
export function bookLetters(book) {
    const b = book.trim();
    const m = /^([A-Za-z])\s*(?:-|–|to)\s*([A-Za-z])$/i.exec(b);
    if (m)
        return letterSet(m[1].toUpperCase() + '-' + m[2].toUpperCase());
    const first = b.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 1);
    return first || ALPHA_KEYS.join('');
}
/** @deprecated use bookLetters; the first and last letters of what the label covers */
export function bookRange(book) {
    const ls = bookLetters(book);
    return { from: ls[0], to: ls[ls.length - 1] };
}
/** A book label as it is stored: a single letter in capitals, anything else as given. */
export const normBookLabel = (book) => { const b = book.trim(); return /^[A-Za-z]$/.test(b) ? b.toUpperCase() : b; };
//# sourceMappingURL=lookup.js.map